import type { WorkspaceServerService } from '@affine/core/modules/cloud';
import type { CacheStorage } from '@affine/core/modules/storage';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { workspacePermissionCacheOwnerKey } from '@affine/core/modules/workspace/utils/permission-cache-identity';
import { LiveData, Service } from '@toeverything/infra';

const WORKSPACE_LOCAL_CACHE_PREFIX = 'workspace-local-cache:v1';
const WORKSPACE_LOCAL_CACHE_VERSION = 1;
const DEFAULT_MAX_ENTRIES_PER_NAMESPACE = 160;
const DEFAULT_MAX_SERIALIZED_CHARS = 512 * 1024;

export interface WorkspaceLocalCacheScope {
  accountId: string;
  cacheOwnerKey: string;
  workspaceId: string;
}

export interface WorkspaceLocalCacheWriteOptions {
  maxEntries?: number;
  maxSerializedChars?: number;
}

export type WorkspaceLocalCacheReadResult<T> =
  | { status: 'absent' }
  | { status: 'read_error'; error: unknown }
  | { status: 'ready'; value: T }
  | { status: 'scope_changed' };

export type WorkspaceLocalCacheRemoveResult =
  | { status: 'absent' }
  | { status: 'remove_error'; error: unknown }
  | { status: 'removed' }
  | { status: 'scope_changed' };

interface WorkspaceLocalCacheEntry<T> extends WorkspaceLocalCacheScope {
  namespace: string;
  segment: string;
  updatedAt: number;
  value: T;
  version: typeof WORKSPACE_LOCAL_CACHE_VERSION;
}

interface WorkspaceLocalCacheIndexEntry {
  segment: string;
  updatedAt: number;
}

interface WorkspaceLocalCacheIndex extends WorkspaceLocalCacheScope {
  entries: WorkspaceLocalCacheIndexEntry[];
  namespace: string;
  version: typeof WORKSPACE_LOCAL_CACHE_VERSION;
}

function encodeKeyPart(value: string): string {
  return encodeURIComponent(value);
}

function namespaceKeyPrefix(
  scope: WorkspaceLocalCacheScope,
  namespace: string
): string {
  return [
    WORKSPACE_LOCAL_CACHE_PREFIX,
    encodeKeyPart(scope.workspaceId),
    encodeKeyPart(scope.cacheOwnerKey),
    encodeKeyPart(namespace),
  ].join(':');
}

export function workspaceLocalCacheEntryKey(
  scope: WorkspaceLocalCacheScope,
  namespace: string,
  segment: string
): string {
  return `${namespaceKeyPrefix(scope, namespace)}:entry:${encodeKeyPart(segment)}`;
}

export function workspaceLocalCacheIndexKey(
  scope: WorkspaceLocalCacheScope,
  namespace: string
): string {
  return `${namespaceKeyPrefix(scope, namespace)}:index`;
}

export function workspaceLocalCacheScopeKey(
  scope: WorkspaceLocalCacheScope | null
): string | null {
  return scope
    ? `${encodeKeyPart(scope.workspaceId)}:${encodeKeyPart(scope.cacheOwnerKey)}`
    : null;
}

function isValidIndex(
  value: WorkspaceLocalCacheIndex | undefined,
  scope: WorkspaceLocalCacheScope,
  namespace: string
): value is WorkspaceLocalCacheIndex {
  return (
    value?.version === WORKSPACE_LOCAL_CACHE_VERSION &&
    value.workspaceId === scope.workspaceId &&
    value.accountId === scope.accountId &&
    value.cacheOwnerKey === scope.cacheOwnerKey &&
    value.namespace === namespace &&
    Array.isArray(value.entries)
  );
}

/**
 * Account- and server-scoped best-effort cache for workspace app projections.
 *
 * Persistence, isolation and retention live here. Domain reconciliation stays
 * in the owning app service (for example Mail or Calendar).
 */
export class WorkspaceLocalCacheService extends Service {
  readonly scope$ = LiveData.computed<WorkspaceLocalCacheScope | null>(get => {
    const workspace = this.workspaceService.workspace;
    const server = get(this.workspaceServerService.server$);
    const account = server ? get(server.account$) : null;
    if (workspace.flavour === 'local' || !server || !account?.id) {
      return null;
    }
    return {
      accountId: account.id,
      workspaceId: workspace.id,
      cacheOwnerKey: workspacePermissionCacheOwnerKey({
        accountId: account.id,
        serverBaseUrl: server.baseUrl,
        serverId: server.id,
      }),
    };
  });

  private readonly writeQueues = new Map<string, Promise<void>>();
  private lastUpdatedAt = 0;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly storage: CacheStorage
  ) {
    super();
  }

  async read<T>(namespace: string, segment: string): Promise<T | undefined> {
    const result = await this.readResult<T>(namespace, segment);
    return result.status === 'ready' ? result.value : undefined;
  }

  async readResult<T>(
    namespace: string,
    segment: string
  ): Promise<WorkspaceLocalCacheReadResult<T>> {
    const scope = this.scope$.value;
    if (!scope || !namespace || !segment) return { status: 'absent' };
    try {
      const entry = await this.storage.get<WorkspaceLocalCacheEntry<T>>(
        workspaceLocalCacheEntryKey(scope, namespace, segment)
      );
      const currentScope = this.scope$.value;
      if (
        currentScope?.workspaceId !== scope.workspaceId ||
        currentScope.accountId !== scope.accountId ||
        currentScope.cacheOwnerKey !== scope.cacheOwnerKey ||
        entry?.version !== WORKSPACE_LOCAL_CACHE_VERSION ||
        entry.workspaceId !== scope.workspaceId ||
        entry.accountId !== scope.accountId ||
        entry.cacheOwnerKey !== scope.cacheOwnerKey ||
        entry.namespace !== namespace ||
        entry.segment !== segment
      ) {
        return workspaceLocalCacheScopeKey(currentScope) !==
          workspaceLocalCacheScopeKey(scope)
          ? { status: 'scope_changed' }
          : { status: 'absent' };
      }
      return { status: 'ready', value: entry.value };
    } catch (error) {
      return { status: 'read_error', error };
    }
  }

  async write<T>(
    namespace: string,
    segment: string,
    value: T,
    options: WorkspaceLocalCacheWriteOptions = {}
  ): Promise<boolean> {
    const scope = this.scope$.value;
    if (!scope || !namespace || !segment) return false;

    const maxSerializedChars =
      options.maxSerializedChars ?? DEFAULT_MAX_SERIALIZED_CHARS;
    try {
      const serialized = JSON.stringify(value);
      if (
        serialized === undefined ||
        serialized.length > Math.max(0, maxSerializedChars)
      ) {
        await this.removeForScope(scope, namespace, segment);
        return false;
      }
    } catch {
      return false;
    }

    const indexKey = workspaceLocalCacheIndexKey(scope, namespace);
    try {
      await this.enqueue(indexKey, async () => {
        const updatedAt = Math.max(Date.now(), this.lastUpdatedAt + 1);
        this.lastUpdatedAt = updatedAt;
        await this.storage.set<WorkspaceLocalCacheEntry<T>>(
          workspaceLocalCacheEntryKey(scope, namespace, segment),
          {
            ...scope,
            namespace,
            segment,
            updatedAt,
            value,
            version: WORKSPACE_LOCAL_CACHE_VERSION,
          }
        );

        const storedIndex =
          await this.storage.get<WorkspaceLocalCacheIndex>(indexKey);
        const entries = isValidIndex(storedIndex, scope, namespace)
          ? storedIndex.entries.filter(entry => entry.segment !== segment)
          : [];
        entries.push({ segment, updatedAt });
        entries.sort((left, right) => right.updatedAt - left.updatedAt);

        const maxEntries = Math.max(
          0,
          options.maxEntries ?? DEFAULT_MAX_ENTRIES_PER_NAMESPACE
        );
        const keep = entries.slice(0, maxEntries);
        for (const entry of entries.slice(maxEntries)) {
          await this.storage.del(
            workspaceLocalCacheEntryKey(scope, namespace, entry.segment)
          );
        }
        await this.storage.set<WorkspaceLocalCacheIndex>(indexKey, {
          ...scope,
          entries: keep,
          namespace,
          version: WORKSPACE_LOCAL_CACHE_VERSION,
        });
      });
      return (
        workspaceLocalCacheScopeKey(this.scope$.value) ===
        workspaceLocalCacheScopeKey(scope)
      );
    } catch {
      return false;
    }
  }

  async remove(namespace: string, segment: string): Promise<void> {
    await this.removeResult(namespace, segment);
  }

  async removeResult(
    namespace: string,
    segment: string
  ): Promise<WorkspaceLocalCacheRemoveResult> {
    const scope = this.scope$.value;
    if (!scope || !namespace || !segment) return { status: 'absent' };
    const key = workspaceLocalCacheEntryKey(scope, namespace, segment);
    try {
      const existed = (await this.storage.get(key)) !== undefined;
      await this.removeForScopeStrict(scope, namespace, segment);
      if (
        workspaceLocalCacheScopeKey(this.scope$.value) !==
        workspaceLocalCacheScopeKey(scope)
      ) {
        return { status: 'scope_changed' };
      }
      return { status: existed ? 'removed' : 'absent' };
    } catch (error) {
      return { status: 'remove_error', error };
    }
  }

  async clearNamespace(namespace: string): Promise<void> {
    const scope = this.scope$.value;
    if (!scope || !namespace) return;
    const prefix = `${namespaceKeyPrefix(scope, namespace)}:`;
    const indexKey = workspaceLocalCacheIndexKey(scope, namespace);
    try {
      await this.enqueue(indexKey, async () => {
        const keys = await this.storage.keys();
        await Promise.all(
          keys
            .filter(key => key.startsWith(prefix))
            .map(key => this.storage.del(key))
        );
      });
    } catch {
      // Cache cleanup is best effort. Domain deletion already happened.
    }
  }

  private async removeForScope(
    scope: WorkspaceLocalCacheScope,
    namespace: string,
    segment: string
  ): Promise<void> {
    try {
      await this.removeForScopeStrict(scope, namespace, segment);
    } catch {
      // Cache cleanup is best effort.
    }
  }

  private async removeForScopeStrict(
    scope: WorkspaceLocalCacheScope,
    namespace: string,
    segment: string
  ): Promise<void> {
    const indexKey = workspaceLocalCacheIndexKey(scope, namespace);
    await this.enqueue(indexKey, async () => {
      await this.storage.del(
        workspaceLocalCacheEntryKey(scope, namespace, segment)
      );
      const storedIndex =
        await this.storage.get<WorkspaceLocalCacheIndex>(indexKey);
      if (!isValidIndex(storedIndex, scope, namespace)) return;
      await this.storage.set<WorkspaceLocalCacheIndex>(indexKey, {
        ...storedIndex,
        entries: storedIndex.entries.filter(entry => entry.segment !== segment),
      });
    });
  }

  private async enqueue(key: string, operation: () => Promise<void>) {
    const previous = this.writeQueues.get(key) ?? Promise.resolve();
    const current = previous.catch(() => undefined).then(operation);
    this.writeQueues.set(key, current);
    try {
      await current;
    } finally {
      if (this.writeQueues.get(key) === current) {
        this.writeQueues.delete(key);
      }
    }
  }
}
